
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { SavedArticle } from '@/hooks/useSavedArticles';
import { Bookmark, BookmarkX, Calendar, Clock, User, AlertTriangle, BookOpen } from 'lucide-react';
import { Skeleton } from '@/components/ui/skeleton';
import { EmptyState } from '@/components/ui/empty-state';
import { formatDistanceToNow } from 'date-fns';

interface SavedArticlesListProps {
  articles: SavedArticle[];
  isLoading: boolean;
  error?: Error | null;
  onUnsave: (articleId: string) => Promise<void>;
}

const SavedArticlesList: React.FC<SavedArticlesListProps> = ({ 
  articles, 
  isLoading,
  error,
  onUnsave
}) => {
  const navigate = useNavigate();

  if (error) {
    return (
      <EmptyState
        icon={<AlertTriangle className="h-12 w-12 text-destructive" />}
        title="Failed to load saved articles"
        description="There was a problem loading your saved articles. Please try again later."
      />
    );
  }

  if (isLoading) {
    return (
      <div className="space-y-4">
        {[1, 2, 3].map((i) => (
          <Card key={i} className="overflow-hidden">
            <CardContent className="p-0">
              <div className="flex flex-col md:flex-row p-4 gap-4">
                <Skeleton className="h-24 w-24 rounded-md" />
                <div className="flex-1 space-y-2">
                  <Skeleton className="h-6 w-3/4" />
                  <Skeleton className="h-4 w-1/2" />
                  <Skeleton className="h-4 w-1/3" />
                  <Skeleton className="h-4 w-full" />
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    );
  }

  if (!articles || articles.length === 0) {
    return (
      <EmptyState
        icon={<Bookmark className="h-12 w-12 text-muted-foreground" />}
        title="No saved articles yet"
        description="Articles you save will appear here."
      />
    );
  }

  return (
    <div className="space-y-4">
      {articles.map((article) => (
        <Card key={article.id} className="overflow-hidden hover:shadow-md transition-shadow">
          <CardContent className="p-0">
            <div className="flex flex-col md:flex-row">
              {article.article.image && (
                <div className="w-full md:w-1/4 h-40 md:h-auto bg-muted">
                  <img
                    src={article.article.image}
                    alt={article.article.title}
                    className="w-full h-full object-cover"
                  />
                </div>
              )}
              <div className="flex-1 p-4 space-y-3">
                <h3 className="text-lg font-medium line-clamp-2">{article.article.title}</h3>
                
                {article.article.lead && (
                  <p className="text-muted-foreground line-clamp-2">
                    {article.article.lead}
                  </p>
                )}
                
                <div className="flex flex-wrap gap-4 text-sm text-muted-foreground">
                  <div className="flex items-center gap-1">
                    <User className="h-4 w-4" />
                    <span>{article.article.author.name}</span>
                  </div>
                  {article.article.publishedAt && (
                    <div className="flex items-center gap-1">
                      <Calendar className="h-4 w-4" />
                      <span>
                        {formatDistanceToNow(new Date(article.article.publishedAt), { addSuffix: true })}
                      </span>
                    </div>
                  )}
                  {article.article.readTime && (
                    <div className="flex items-center gap-1">
                      <Clock className="h-4 w-4" />
                      <span>{article.article.readTime} min read</span>
                    </div>
                  )}
                </div>
                
                <div className="pt-2 flex gap-2">
                  <Button
                    variant="default"
                    size="sm"
                    onClick={() => navigate(`/article/${article.article.id}`)}
                  >
                    <BookOpen className="mr-2 h-4 w-4" />
                    Read article
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => onUnsave(article.article.id)}
                  >
                    <BookmarkX className="mr-2 h-4 w-4" />
                    Remove from saved
                  </Button>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  );
};

export default SavedArticlesList;
